/**
 * src/pages/homepage/components/V5HomePage.jsx
 *
 * Complete V5 Home page, mirrors praesidio-care-v5(1).html #page-home.
 * Sections (in order):
 *   Hero → Logo Strip → Marquee → What We Engineer →
 *   Featured Products → Proof + Process → Why Us →
 *   Pricing → Testimonials → FAQ → CTA → Footer
 *
 * Notes:
 * - Canvas, chart bars, and GSAP animations are wired in useEffect via v5Shell
 * - All showPage() calls replaced with <Link to="..."> or useNavigate()
 * - DO NOT change any form field names or Web3Forms keys
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  animateHero,
  initHeroCanvas,
  initChartBars,
  initLogoStrip,
  setupScrollReveal,
  startTestiTimer,
  testiGo,
  testiNav,
  toggleFaq,
} from '../../../utils/v5Shell';
import V5Footer from '../../../components/V5Footer';

// ─── DATA ────────────────────────────────────────────────────────────────────

const LOGOS = [
  'IBM','Deloitte','Hulu','Bain & Company','Snapchat','Lyft',
  'CVS Health','Temu','Taco Bell','Depop','Glossier','Beli','Prequel',
  // duplicate for seamless loop
  'IBM','Deloitte','Hulu','Bain & Company','Snapchat','Lyft',
  'CVS Health','Temu','Taco Bell','Depop','Glossier','Beli','Prequel',
];

const MARQUEE_WORDS = [
  { text: 'Super Intelligence', hi: false },
  { text: 'SaaS Platforms', hi: true },
  { text: 'Viral Apps', hi: false },
  { text: 'Automation', hi: true },
  { text: 'IoT & Edge', hi: false },
  { text: 'Products', hi: true },
  // duplicate
  { text: 'Super Intelligence', hi: false },
  { text: 'SaaS Platforms', hi: true },
  { text: 'Viral Apps', hi: false },
  { text: 'Automation', hi: true },
  { text: 'IoT & Edge', hi: false },
  { text: 'Products', hi: true },
];

const ENG_CARDS = [
  { num: '01', icon: 'agents', title: 'Super Intelligence Agents', desc: 'Agents that reason, retrieve, and stay inside a guardrail. A prompt anyone can write is not this. Evals a person can read, from the first week.', link: '/capabilities/ai-data-platforms' },
  { num: '02', icon: 'saas', title: 'SaaS & Platform Engineering', desc: 'Multi-tenant platforms with auth, billing, dashboards, APIs. Cloud-native, built to handle 10x overnight traffic spikes.', link: '/capabilities/mobile-web-engineering' },
  { num: '03', icon: 'apps', title: 'Viral Consumer Apps', desc: 'Mobile and web apps with engagement loops, push mechanics, and growth hooks. Engineered for retention, built for scale.', link: '/capabilities/computer-vision-wearables' },
  { num: '04', icon: 'iot', title: 'Edge Super Intelligence', desc: 'Reasoning on the device, shown on a live dashboard. Crowd density, parking, wearables. The judgment happens in the room, not in a distant demo.', link: '/capabilities/iot-sensors-edge' },
  { num: '05', icon: 'data', title: 'Data for Super Intelligence', desc: 'The memory underneath the reasoning: pipelines, live analytics, training, and serving. Production infrastructure, not a notebook.', link: '/capabilities/ai-data-platforms' },
  { num: '06', icon: 'ops', title: 'DevOps & Cloud Infrastructure', desc: 'CI/CD pipelines, Kubernetes, auto-scaling, monitoring. Your product stays alive when the world shows up at once.', link: '/capabilities/mobile-web-engineering' },
];

function CraftIcon({ name }) {
  const paths = {
    agents: (
      <>
        <circle cx="6" cy="7" r="2" />
        <circle cx="18" cy="7" r="2" />
        <circle cx="12" cy="17" r="2" />
        <path d="M8 7h8M7.4 8.7 10.6 15.2M16.6 8.7 13.4 15.2" />
      </>
    ),
    saas: (
      <>
        <path d="M4 8.5 12 4l8 4.5-8 4.5L4 8.5Z" />
        <path d="M4 12.5 12 17l8-4.5" />
        <path d="M4 16.5 12 21l8-4.5" />
      </>
    ),
    apps: (
      <>
        <rect x="7" y="3" width="10" height="18" rx="2" />
        <path d="M11 18h2" />
      </>
    ),
    iot: (
      <>
        <circle cx="12" cy="12" r="2" />
        <path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7" />
        <path d="M6 6a8.5 8.5 0 0 0 0 12M18 6a8.5 8.5 0 0 1 0 12" />
      </>
    ),
    data: (
      <>
        <path d="M4 19V5" />
        <path d="M4 19h16" />
        <path d="M8 15v-3M12 15V8M16 15v-5" />
      </>
    ),
    ops: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="8.5" y="14" width="7" height="7" rx="1" />
        <path d="M6.5 10v2.5h5.5M17.5 10v2.5H12" />
      </>
    ),
  };
  return (
    <svg className="ec-svg" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

const PRODUCTS = [
  {
    href: 'https://www.getbyonco.com/',
    name: 'ByOnco',
    title: 'ByOnco · Cancer Care Navigation',
    desc: 'Super Intelligence that routes a cancer patient to the right specialist, hospital, and next step across India.',
    stack: ['React', 'Node.js', 'Python', 'LLM/RAG', 'PostgreSQL', 'AWS'],
    badge: 'Live',
    badgeClass: 'pb-live',
    metrics: [
      ['1200+', 'Hospitals'],
      ['2.1M', 'Patients Reached'],
      ['2×', 'Care Adherence'],
    ],
    shot: 'bo',
  },
  {
    href: '/products/surakshapath',
    name: 'SurakshaPath',
    title: 'SurakshaPath · Smart Crowd Safety',
    desc: 'Real-time crowd density analytics with computer vision, smart LED blinker guidance, and predictive stampede alerts for mass gatherings.',
    stack: ['Python', 'OpenCV', 'YOLO', 'IoT', 'React', 'GCP'],
    badge: 'Pilot',
    badgeClass: 'pb-pilot',
    metrics: [
      ['−35%', 'Crowd Risk'],
      ['9', 'Cities Piloted'],
    ],
    shot: 'sp',
  },
  {
    href: 'https://www.vayuglasses.in/',
    name: 'Vayu',
    title: 'Vayu AI Glasses',
    desc: 'Essentials, MedPro, and LegalEdge. Answers, translation, and professional context in the lens. Pre-order at vayuglasses.in.',
    stack: ['Essentials', 'MedPro', 'LegalEdge', 'On-device', 'No camera'],
    badge: 'Pre-order',
    badgeClass: 'pb-mvp',
    metrics: [
      ['3', 'Lines'],
      ['<100ms', 'Inference'],
    ],
    shot: 'vx',
  },
  {
    href: '/products/parkingkar',
    name: 'Parkingkar',
    title: 'Parkingkar · Smart City Mobility',
    desc: 'IoT sensor network with demand-shaping ML algorithms routing drivers to open parking slots in real time. Reduced cruising by 28%.',
    stack: ['LoRaWAN', 'Python', 'React Native', 'Redis', 'AWS'],
    badge: 'Pilot',
    badgeClass: 'pb-pilot',
    metrics: [
      ['1.8M', 'Impact Scale'],
      ['−28%', 'Cruising'],
    ],
    shot: 'pk',
  },
];

function ProductShot({ kind, name }) {
  if (kind === 'bo') {
    return (
      <div className="shot" aria-hidden="true">
        <div className="shot-top"><span>{name}</span><span>Queue</span></div>
        <div className="shot-line"><b>New referral</b><i>Oncology</i></div>
        <div className="shot-line is-hot"><b>Match ready</b><i>2 hospitals</i></div>
        <div className="shot-line"><b>Follow-up</b><i>Awaiting slot</i></div>
      </div>
    );
  }
  if (kind === 'sp') {
    return (
      <div className="shot" aria-hidden="true">
        <div className="shot-top"><span>{name}</span><span>Density</span></div>
        <div className="shot-bars" aria-hidden="true">
          <span style={{ height: '42%' }} /><span style={{ height: '68%' }} /><span className="is-hot" style={{ height: '88%' }} /><span style={{ height: '54%' }} /><span style={{ height: '36%' }} />
        </div>
        <div className="shot-line"><b>Gate 3</b><i>Hold</i></div>
      </div>
    );
  }
  if (kind === 'vx') {
    return (
      <div className="shot shot-hud" aria-hidden="true">
        <div className="shot-top"><span>{name}</span><span>87 ms</span></div>
        <div className="shot-hud-read">
          <b>Vitals overlay</b>
          <i>On device</i>
        </div>
        <div className="shot-line"><b>Voice</b><i>Ready</i></div>
      </div>
    );
  }
  return (
    <div className="shot" aria-hidden="true">
      <div className="shot-top"><span>{name}</span><span>Bays</span></div>
      <div className="shot-bays">
        <span className="is-open">A1</span><span>A2</span><span className="is-open">A3</span><span>B1</span><span className="is-open">B2</span><span>B3</span>
      </div>
      <div className="shot-line"><b>Open nearby</b><i>3 bays</i></div>
    </div>
  );
}

const TESTIMONIALS = [
  {
    quote: '"The Praesidio team shipped the Super Intelligence behind our cancer navigation in under 5 weeks. They didn\'t just write code; they understood the domain, challenged our assumptions, and delivered something that actually works in hospitals. I\'ve never worked with an engineering team this good."',
    name: 'Dr. Ravi Mishra',
    role: 'Director, ByOnco Labs · Oncology Navigation Platform',
    initials: 'RM',
    avatarBg: 'rgba(200,255,0,.08)',
  },
  {
    quote: '"We brought them a crowd safety problem that had stumped three other vendors. In 6 weeks they had Super Intelligence running on the device, a live city dashboard, and LED actuation working end-to-end. They\'re the only team I know who can take a hard real-world problem and actually ship a solution."',
    name: 'Abhay Nair',
    role: 'Head of Innovation, SurakshaPath · Smart City Safety',
    initials: 'AN',
    avatarBg: 'rgba(100,180,255,.08)',
  },
  {
    quote: '"Most agencies promise fast delivery and then disappear into scope creep. Praesidio gave us a fixed price, hit every sprint, and deployed a multi-city IoT platform that our municipal partners actually use. The 60–70% cost advantage over US alternatives is real, and the quality is better."',
    name: 'Priya Subramaniam',
    role: 'CTO, Parkingkar · Smart Mobility Platform',
    initials: 'PS',
    avatarBg: 'rgba(255,189,46,.08)',
  },
  {
    quote: '"I came in with a rough concept for AR smart glasses for clinicians. They went from prototype to on-device inference running at under 100ms in four weeks. The product we shipped is already being piloted in three major hospitals. Praesidio is not just fast; they\'re frighteningly good."',
    name: 'Kiran Desai',
    role: 'Lead Innovator, Vayu AI Glasses',
    initials: 'KD',
    avatarBg: 'rgba(200,100,255,.08)',
  },
];

const FAQS = [
  {
    q: 'How do you deliver a product in 4 weeks?',
    a: 'We run 2-week sprints with a full-stack squad: engineers, designers, and Super Intelligence, working in parallel. We front-load architecture on day one so there is no planning drag mid-sprint. By week 2 you have working software; by week 4 you are in production.',
  },
  {
    q: 'What is included in the $5K Pilot tier?',
    a: 'The $5K Pilot is a 2-week sprint delivering one focused, working proof-of-concept: a Super Intelligence agent that does one job, a functional MVP screen, or an IoT sensor pilot. You get source code, a staging deployment, and a written handover document. No upsell pressure; many clients stop here and build internally.',
  },
  {
    q: 'Do you work with European and American clients?',
    a: 'Yes. Approximately 60% of our active engagements are with EU and US clients. We overlap with European business hours daily and US time zones on request. All contracts are in USD or EUR, invoiced to your country with standard international wire or Stripe payment.',
  },
  {
    q: 'How do you handle data privacy and IP ownership?',
    a: 'All IP transfers fully to you upon final payment. We operate under strict NDA from day one. Client data is never retained, shared, or used to train any model. We are GDPR-compliant and can sign a DPA for EU engagements. Codebase is delivered via your own GitHub/GitLab organisation.',
  },
  {
    q: 'What makes you different from a traditional dev agency?',
    a: 'Three things: (1) We ship real products into real environments, not polished demos. (2) We price by outcome, not by the hour; every engagement is fixed-price. (3) Our engineers have built 40+ live products across healthcare, smart cities, climate, and defense; they understand complex domains, not just code.',
  },
  {
    q: 'Can you integrate with our existing tech stack?',
    a: 'Yes. We\'ve integrated with everything from legacy hospital EHR systems to modern SaaS APIs. We support all major cloud providers (AWS, GCP, Azure), any relational or NoSQL database, and can build custom connectors. If it has an API, we can work with it.',
  },
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function V5HomePage() {
  const [dockOn, setDockOn] = useState(false);

  useEffect(() => {
    const onScroll = () => setDockOn(window.scrollY > Math.min(520, window.innerHeight * 0.7));
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    initHeroCanvas();
    initChartBars();
    initLogoStrip();
    setupScrollReveal();
    startTestiTimer();
    animateHero();
  }, []);

  return (
    <>
      {/* ══════════════════════ HERO ══════════════════════ */}
      <section className="hero">
        <canvas id="heroCanvas" />
        <div className="hero-fade-b" />

        <div className="hero-inner">
          {/* LEFT */}
          <div className="hero-left">
            <h1 id="heroTitle">
              From the brief to a product that <span className="hwc-accent">ships.</span>
            </h1>

            <p className="hero-p">
              We take a brief and ship working software. Super Intelligence, apps, and platforms,
              in production, on a fixed price. A prompt is not a product. What we ship has to reason,
              and a person has to be able to check it.
            </p>

            <div className="hero-row">
              <Link to="/contact?intent=call" className="h-btn h-primary">Book a call</Link>
              <a href="#pricing" className="h-btn h-quiet">Compare pricing</a>
            </div>

            <div className="hero-metrics">
              <div className="hm-item">
                <div className="hm-val">40+</div>
                <div className="hm-lbl">Products Shipped</div>
              </div>
              <div className="hm-item">
                <div className="hm-val">12</div>
                <div className="hm-lbl">Countries</div>
              </div>
              <div className="hm-item">
                <div className="hm-val">4wk</div>
                <div className="hm-lbl">Avg. to Launch</div>
              </div>
            </div>
          </div>

          {/* RIGHT: Live Dashboard */}
          <div className="hero-right">
            <div className="hdash">
              <div className="hdash-bar">
                <div className="dot r" /><div className="dot y" /><div className="dot g" />
                <span className="db-title">praesidio · production · live</span>
                <div className="db-live">
                  <div className="db-live-dot" />LIVE
                </div>
              </div>
              <div className="hdash-body">
                <div className="hd-top-row">
                  <div className="hd-stat">
                    <div className="hd-label">Products Live</div>
                    <div className="hd-val">12</div>
                    <div className="hd-sub"><span className="up">↑ 3</span> this quarter</div>
                  </div>
                  <div className="hd-stat">
                    <div className="hd-label">Uptime SLA</div>
                    <div className="hd-val">99.95%</div>
                    <div className="hd-sub">30-day rolling</div>
                  </div>
                  <div className="hd-stat">
                    <div className="hd-label">Deploys</div>
                    <div className="hd-val">847</div>
                    <div className="hd-sub">this year</div>
                  </div>
                </div>
                <div className="hd-chart-box">
                  <div className="hd-chart-head">
                    <span className="hc-name">Build Velocity · 24 sprints</span>
                    <span className="hc-tag">+34% YoY</span>
                  </div>
                  <div className="chart-bars" id="chartBars" />
                </div>
                <div className="hd-products">
                  <div className="hd-prod">
                    <div className="hp-mark">Bo</div>
                    <div><div className="hp-name">ByOnco</div><div className="hp-desc">Cancer-care navigation</div></div>
                    <span className="hp-status hp-live">Live</span>
                  </div>
                  <div className="hd-prod">
                    <div className="hp-mark">Sp</div>
                    <div><div className="hp-name">SurakshaPath</div><div className="hp-desc">Crowd safety</div></div>
                    <span className="hp-status hp-pilot">Pilot</span>
                  </div>
                  <div className="hd-prod">
                    <div className="hp-mark">Vx</div>
                    <div><div className="hp-name">Vayu</div><div className="hp-desc">AI Glasses</div></div>
                    <span className="hp-status hp-mvp">MVP</span>
                  </div>
                </div>
                <div className="hd-log">
                  <div className="hl-title">Recent Activity</div>
                  <div className="hl-entry"><span className="hl-dot g" />ByOnco v3.2 deployed to prod<span className="hl-time">2m ago</span></div>
                  <div className="hl-entry"><span className="hl-dot b" />SurakshaPath model retrained<span className="hl-time">18m ago</span></div>
                  <div className="hl-entry"><span className="hl-dot y" />Parkingkar sensor grid expanded<span className="hl-time">1h ago</span></div>
                  <div className="hl-entry"><span className="hl-dot g" />Vayu AI Glasses inference → 87ms<span className="hl-time">3h ago</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-scroll" id="heroScroll">
          <span className="hs-text">SCROLL</span>
          <div className="hs-line" />
        </div>
      </section>

      {/* ══════════════════════ LOGO STRIP ══════════════════════ */}
      <div className="logo-strip">
        <div className="ls-label">Trusted Technology Partners</div>
        <div id="logoTrack" className="ls-track">
          {LOGOS.map((l, i) => <span key={i}>{l}</span>)}
        </div>
      </div>

      {/* ══════════════════════ MARQUEE ══════════════════════ */}
      <div className="marquee-band">
        <div id="mbTrack" className="mb-track">
          {MARQUEE_WORDS.map((w, i) => (
            <span key={i} className={w.hi ? 'hi' : ''}>{w.text}</span>
          ))}
        </div>
      </div>

      {/* ══════════════════════ WHAT WE ENGINEER ══════════════════════ */}
      <section className="section">
        <div className="sec-head">
          <div className="s-eyebrow">What We Engineer</div>
          <h2 className="s-title">A proof-of-concept studio for the <em>Super Intelligence era.</em></h2>
          <p className="s-sub">
            Anyone can generate a demo. Super Intelligence is what still has to reason in a hospital,
            on a street, or on a device, and fail closed when it is wrong. Every engagement ends in
            production code, live users, and a number you can check.
          </p>
        </div>
        <div className="eng-grid">
          {ENG_CARDS.map(c => (
            <Link key={c.num} to={c.link} className="eng-card" style={{ textDecoration: 'none' }}>
              <div className="ec-line" />
              <div className="ec-num">{c.num}</div>
              <div className="ec-icon"><CraftIcon name={c.icon} /></div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
            </Link>
          ))}
        </div>
        <p className="path-note">
          <a href="#pricing">Compare the three scopes</a>
          <span> if you want a number before a call.</span>
        </p>
      </section>

      {/* ══════════════════════ FEATURED PRODUCTS ══════════════════════ */}
      <section className="section dark">
        <div className="sec-head">
          <div className="s-eyebrow">Featured Products</div>
          <h2 className="s-title">Shipped. Deployed. <em>In production.</em></h2>
          <p className="s-sub">These aren't portfolio pieces. They're live products used by real people every day.</p>
        </div>
        <div className="prod-rail" aria-label="Featured products">
          <div className="prod-rail-track">
            {[0, 1].map((copy) => (
              <div className="prod-grid prod-grid-slide" key={copy} aria-hidden={copy === 1 ? true : undefined} inert={copy === 1 ? true : undefined}>
          <a href="https://www.getbyonco.com/" className="prod-card" target="_blank" rel="noopener noreferrer" tabIndex={copy === 1 ? -1 : undefined}>
            <div className="prod-vis">
              <ProductShot kind="bo" name="ByOnco" />
              <span className="prod-badge pb-live"><span className="pb-bdot" />Live</span>
            </div>
            <div className="prod-body">
              <h3>ByOnco · Cancer Care Navigation</h3>
              <p className="prod-desc">Super Intelligence that routes a cancer patient to the right specialist, hospital, and next step across India.</p>
              <div className="prod-stack"><span>React</span><span>Node.js</span><span>Python</span><span>LLM/RAG</span><span>PostgreSQL</span><span>AWS</span></div>
              <div className="prod-metrics">
                <div><div className="pm-val">1200+</div><div className="pm-lbl">Hospitals</div></div>
                <div><div className="pm-val">2.1M</div><div className="pm-lbl">Patients Reached</div></div>
                <div><div className="pm-val">2×</div><div className="pm-lbl">Care Adherence</div></div>
              </div>
              <span className="prod-go">Open ByOnco</span>
            </div>
          </a>
          <Link to="/products/surakshapath" className="prod-card" tabIndex={copy === 1 ? -1 : undefined}>
            <div className="prod-vis">
              <ProductShot kind="sp" name="SurakshaPath" />
              <span className="prod-badge pb-pilot"><span className="pb-bdot" />Pilot</span>
            </div>
            <div className="prod-body">
              <h3>SurakshaPath · Smart Crowd Safety</h3>
              <p className="prod-desc">Real-time crowd density analytics with computer vision, smart LED blinker guidance, and predictive stampede alerts for mass gatherings.</p>
              <div className="prod-stack"><span>Python</span><span>OpenCV</span><span>YOLO</span><span>IoT</span><span>React</span><span>GCP</span></div>
              <div className="prod-metrics">
                <div><div className="pm-val">−35%</div><div className="pm-lbl">Crowd Risk</div></div>
                <div><div className="pm-val">9</div><div className="pm-lbl">Cities Piloted</div></div>
              </div>
              <span className="prod-go">Open SurakshaPath</span>
            </div>
          </Link>
          <a href="https://www.vayuglasses.in/" className="prod-card" target="_blank" rel="noopener noreferrer" tabIndex={copy === 1 ? -1 : undefined}>
            <div className="prod-vis">
              <ProductShot kind="vx" name="Vayu" />
              <span className="prod-badge pb-mvp"><span className="pb-bdot" />Pre-order</span>
            </div>
            <div className="prod-body">
              <h3>Vayu AI Glasses</h3>
              <p className="prod-desc">Essentials, MedPro, and LegalEdge. Answers, translation, and professional context in the lens.</p>
              <div className="prod-stack"><span>Essentials</span><span>MedPro</span><span>LegalEdge</span><span>On-device</span><span>No camera</span></div>
              <div className="prod-metrics">
                <div><div className="pm-val">3</div><div className="pm-lbl">Lines</div></div>
                <div><div className="pm-val">&lt;100ms</div><div className="pm-lbl">Inference</div></div>
              </div>
              <span className="prod-go">Open vayuglasses.in</span>
            </div>
          </a>
          <Link to="/products/parkingkar" className="prod-card" tabIndex={copy === 1 ? -1 : undefined}>
            <div className="prod-vis">
              <ProductShot kind="pk" name="Parkingkar" />
              <span className="prod-badge pb-pilot"><span className="pb-bdot" />Pilot</span>
            </div>
            <div className="prod-body">
              <h3>Parkingkar · Smart City Mobility</h3>
              <p className="prod-desc">IoT sensor network with demand-shaping ML algorithms routing drivers to open parking slots in real time. Reduced cruising by 28%.</p>
              <div className="prod-stack"><span>LoRaWAN</span><span>Python</span><span>React Native</span><span>Redis</span><span>AWS</span></div>
              <div className="prod-metrics">
                <div><div className="pm-val">1.8M</div><div className="pm-lbl">Impact Scale</div></div>
                <div><div className="pm-val">−28%</div><div className="pm-lbl">Cruising</div></div>
              </div>
              <span className="prod-go">Open Parkingkar</span>
            </div>
          </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════ PROOF + PROCESS ══════════════════════ */}
      <section className="section" id="proof">
        <div className="proof-layout">
          <div>
            <div className="s-eyebrow">Proof of Output</div>
            <h2 className="s-title">Numbers. Process.<br /><em>No hand-waving.</em></h2>
            <p className="s-sub">We build things that work in the real world: hospitals, highways, festival grounds. If it works here, it works anywhere.</p>
            <div className="stat-grid">
              <div className="sg-item"><div className="sg-val" data-count="40" data-suf="+">0</div><div className="sg-lbl">Products shipped</div><div className="sg-sub">Healthcare · Mobility · Safety</div></div>
              <div className="sg-item"><div className="sg-val" data-count="12">0</div><div className="sg-lbl">Countries served</div><div className="sg-sub">US · EU · India · SEA</div></div>
              <div className="sg-item"><div className="sg-val" data-count="98" data-suf="%">0%</div><div className="sg-lbl">Client retention</div><div className="sg-sub">They always come back</div></div>
              <div className="sg-item"><div className="sg-val">99.95%</div><div className="sg-lbl">Uptime SLA</div><div className="sg-sub">E2E encrypted · SOC 2 ready</div></div>
            </div>
          </div>
          <div>
            <div className="s-eyebrow">How We Work</div>
            <h2 className="s-title">From idea to production<br /><em>in weeks.</em></h2>
            <p className="s-sub">Four phases, zero bloat. We move fast because we&apos;ve done this forty times, and because your users can&apos;t wait.</p>
            <div className="proc-list">
              <div className="pr-step"><div className="pr-num">01</div><div><h4>Discovery &amp; Scope</h4><p>48-hour audit. User flows mapped, testable slice defined, scope locked before a single line of code.</p></div></div>
              <div className="pr-step"><div className="pr-num">02</div><div><h4>Design Sprint</h4><p>High-fidelity prototypes in 3–5 days. Clickable flows and data contracts agreed before production begins.</p></div></div>
              <div className="pr-step"><div className="pr-num">03</div><div><h4>Build &amp; Deploy</h4><p>Daily standups. Weekly deploys. Real users on the product within 2–3 weeks of kick-off. CI/CD from day one.</p></div></div>
              <div className="pr-step"><div className="pr-num">04</div><div><h4>Measure &amp; Scale</h4><p>Analytics, A/B tests, performance tuning. We stay until the product finds its groove, and beyond.</p></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════ WHY US ══════════════════════ */}
      <section className="section dark">
        <div className="sec-head">
          <div className="s-eyebrow">Why Praesidio Care</div>
          <h2 className="s-title">Global cost advantage.<br /><em>Silicon Valley&apos;s standards.</em></h2>
          <p className="s-sub">A product studio in San Francisco and New York, with the craft, speed, and taste of the best teams anywhere in the world.</p>
        </div>
        <div className="why-grid">
          <div className="why-card"><div className="wc-num">01</div><h3>60–70% Cost Advantage</h3><p>World-class engineering at a fraction of US/EU rates. Same timezone overlap. None of the compromise on quality, speed, or communication.</p></div>
          <div className="why-card"><div className="wc-num">02</div><h3>Proof-of-Concept First</h3><p>We don't ask for 6-month contracts upfront. Start with a paid 4-week pilot. If it works, we scale. If not, you paid only for learning, not for promises.</p></div>
          <div className="why-card"><div className="wc-num">03</div><h3>Full-Stack Product Team</h3><p>Design, frontend, backend, mobile, DevOps, and Super Intelligence, all under one roof. No juggling 5 vendors, 12 Slack channels, and 3 project managers.</p></div>
          <div className="why-card"><div className="wc-num">04</div><h3>Battle-Tested in Chaos</h3><p>We've shipped products in hospitals, highways, and festival grounds with 100K+ people. If it works there, it works in your enterprise environment.</p></div>
        </div>
      </section>

      {/* ══════════════════════ PRICING ══════════════════════ */}
      <section className="section" id="pricing">
        <div className="sec-head">
          <div className="s-eyebrow">Pricing</div>
          <h2 className="s-title">Transparent. Flexible.<br /><em>No surprises.</em></h2>
          <p className="s-sub">From a 4-week pilot to a dedicated engineering squad, we scale with your ambition and your budget.</p>
        </div>
        <div className="pricing-grid">
          <div className="p-tier">
            <div className="p-name">Pilot</div>
            <div className="p-price">$5K <span>/ project</span></div>
            <div className="p-desc">4-week proof-of-concept. Validate your idea with a working product and real users.</div>
            <ul className="p-list">
              <li>Working MVP in 4 weeks</li><li>UI/UX design included</li>
              <li>1 platform (web or mobile)</li><li>Analytics &amp; monitoring</li>
              <li>30 days post-launch support</li>
            </ul>
            <Link to="/contact?intent=pilot" className="p-btn">Start the pilot</Link>
          </div>
          <div className="p-tier feat">
            <div className="p-name">Growth</div>
            <div className="p-price">$15K <span>/ project</span></div>
            <div className="p-desc">Full-stack build with Super Intelligence, multi-platform, and scale-ready architecture from day one.</div>
            <ul className="p-list">
              <li>Full product in 6–8 weeks</li><li>Web + Mobile (iOS &amp; Android)</li>
              <li>Super Intelligence, wired in</li><li>Admin dashboard &amp; APIs</li>
              <li>CI/CD + cloud infrastructure</li><li>90 days post-launch support</li>
            </ul>
            <Link to="/contact?intent=growth" className="p-btn">Plan the build</Link>
          </div>
          <div className="p-tier">
            <div className="p-name">Enterprise</div>
            <div className="p-price">Custom</div>
            <div className="p-desc">Dedicated engineering squad, ongoing development, enterprise compliance and long-term partnership.</div>
            <ul className="p-list">
              <li>Dedicated engineering squad</li><li>Custom Super Intelligence &amp; automation</li>
              <li>SOC 2 security &amp; compliance</li><li>99.95% uptime SLA</li>
              <li>Priority support &amp; account manager</li>
            </ul>
            <Link to="/contact?intent=enterprise" className="p-btn">Talk to a lead</Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════ TESTIMONIALS ══════════════════════ */}
      <section className="testi-section section">
        <div className="sec-head">
          <div className="s-eyebrow">What Clients Say</div>
          <h2 className="s-title">We don't just claim.<br /><em style={{ fontStyle: 'italic', color: 'var(--accent)' }}>We prove.</em></h2>
          <p className="s-sub">Real feedback from the people who trusted us to build their most important systems.</p>
        </div>
        <div className="testi-carousel" id="testiCarousel">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className={`testi-slide${i === 0 ? ' active' : ''}`}>
              <div className="testi-quote">{t.quote}</div>
              <div className="testi-author">
                <div className="ta-avatar ta-mono" aria-hidden="true">{t.initials}</div>
                <div>
                  <div className="ta-name">{t.name}</div>
                  <div className="ta-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="testi-nav">
          <button type="button" className="tn-arrow" aria-label="Previous testimonial" onClick={() => testiNav(-1)}>←</button>
          {TESTIMONIALS.map((t, i) => (
            <button key={i} type="button" className={`tn-dot${i === 0 ? ' active' : ''}`} aria-label={`Show testimonial from ${t.name}`} onClick={() => testiGo(i)} />
          ))}
          <button type="button" className="tn-arrow" aria-label="Next testimonial" onClick={() => testiNav(1)}>→</button>
        </div>
      </section>

      {/* ══════════════════════ FAQ ══════════════════════ */}
      <section className="faq-section section">
        <div className="s-eyebrow">Common Questions</div>
        <div className="faq-layout">
          <div className="faq-left">
            <h2>Everything you need to <em>know.</em></h2>
            <p>Still have questions? Book a free 30-minute strategy call and we'll answer everything in real time.</p>
            <Link to="/contact?intent=call" className="faq-cta">Book a call</Link>
            <a href="#pricing" className="faq-alt">Or compare pricing</a>
          </div>
          <div className="faq-items">
            {FAQS.map((f, i) => (
              <div key={i} className="faq-item">
                <button className="faq-q" onClick={e => toggleFaq(e.currentTarget)}>
                  {f.q}
                  <div className="faq-icon">+</div>
                </button>
                <div className="faq-a"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════ CTA ══════════════════════ */}
      <div className="cta-section">
        <h2>Ready to ship your <em>next big idea?</em></h2>
        <p>Join the companies and founders who trust Praesidio Care to turn ambitious ideas into production-grade products in 4 weeks, on a fixed price.</p>
        <div className="cta-actions">
          <Link to="/contact?intent=call" className="cta-btn" id="ctaBtn">
            Book a strategy call
          </Link>
          <Link to="/contact?intent=brief" className="cta-btn cta-btn-ghost">
            Send a written brief
          </Link>
        </div>
      </div>

      <div className={`m-dock${dockOn ? ' is-on' : ''}`}>
        <a href="#pricing">Pricing</a>
        <Link to="/contact?intent=call">Book a call</Link>
      </div>

      <V5Footer />
    </>
  );
}

